// Contratos de "Analytics do site": Google Analytics 4 conectado por empresa.

export const ANALYTICS_DIMS = ['channel', 'source_medium', 'campaign', 'landing_page', 'device', 'city', 'key_event'] as const
export type AnalyticsDim = (typeof ANALYTICS_DIMS)[number]

export const ANALYTICS_DIM_LABELS: Record<AnalyticsDim, string> = {
  channel: 'Canais',
  source_medium: 'Origem / mídia',
  campaign: 'Campanhas',
  landing_page: 'Páginas de entrada',
  device: 'Aparelhos',
  city: 'Cidades',
  key_event: 'Eventos-chave',
}

/** Propriedade GA4 que a pessoa pode escolher. */
export interface AnalyticsPropertyDto { id: string; name: string; accountName: string }
export interface AnalyticsPropertiesResponse { items: AnalyticsPropertyDto[] }

export interface AnalyticsSourceDto {
  id: string
  propertyId: string
  propertyName: string
  accountName: string | null
  status: 'active' | 'needs_reconnect' | 'error'
  /** Importando o histórico (13 meses) */
  backfilling: boolean
  lastSyncAt: string | null
  lastError: string | null
  connectedBy: string | null
  currency: string
}

export interface AnalyticsTotals {
  sessions: number
  totalUsers: number
  newUsers: number
  engagedSessions: number
  /** engagedSessions / sessions (0 a 1) */
  engagementRate: number
  /** Tempo médio de engajamento por sessão, em segundos */
  avgEngagementSeconds: number
  pageViews: number
  keyEvents: number
  /** Em reais (moeda da propriedade) */
  revenue: number
}

export interface AnalyticsTopRow { key: string; sessions: number; totalUsers: number; keyEvents: number; revenue: number }

/** GET /companies/:id/analytics/summary?days=7|30|90 */
export interface AnalyticsSummaryDto {
  source: AnalyticsSourceDto | null
  days: number
  current: AnalyticsTotals
  /** Mesmo número de dias, imediatamente antes */
  previous: AnalyticsTotals
  byDay: Array<{ day: string; sessions: number; keyEvents: number }>
  /** Até 10 linhas por dimensão; em `key_event`, `keyEvents` é a contagem do evento */
  top: Record<AnalyticsDim, AnalyticsTopRow[]>
}

/** Uma campanha de anúncio com o que aconteceu no site. Dinheiro na moeda da conta de anúncios (`currency`). */
export interface AnalyticsCampaignRow {
  campaignId: string
  name: string
  provider: 'google_ads' | 'meta' | 'tiktok' | 'openai_ads' | 'fake'
  /** Moeda da conta de anúncios (ISO 4217); gasto e custos vêm nela, sem conversão */
  currency: string
  spend: number
  clicks: number
  /** Resultado que a plataforma declara */
  results: number
  /** Visitas do GA4 com o utm_campaign (ou nome) desta campanha; null = Google Analytics não conectado */
  sessions: number | null
  keyEvents: number | null
  /** Contatos que chegaram pelas landing pages com o utm_campaign desta campanha */
  contacts: number
  /** Visitas ÷ cliques (0 a 1); null sem GA ou sem cliques */
  clickToVisit: number | null
  /** Gasto ÷ contatos das landing pages */
  costPerContact: number | null
  /** Gasto ÷ eventos-chave do GA */
  costPerKeyEvent: number | null
}

/** GET /companies/:id/analytics/campaigns?days=7|30|90 */
export interface AnalyticsCampaignsDto {
  days: number
  from: string
  to: string
  /** Google Analytics conectado */
  hasAnalytics: boolean
  rows: AnalyticsCampaignRow[]
  /** O que não deu para ligar a nenhuma campanha (link sem UTM, tráfego orgânico...) */
  unmatched: { sessions: number | null; keyEvents: number | null; contacts: number }
}
